import z from "zod";

import { type Patch, patch } from "../../api-canvas-metadata/Patch";
import { transactionProcessedSuccess } from "../../api-canvas-metadata/TransactionProcessedEvent";
import {
	type CanvasSpecifier,
	canvasSpecifier,
	disconnectMessageBroadcast,
	pongMessage,
} from "../../api-types";
import { UnauthorizedError, accessToken, createHttpHeaders, post } from "../../http";
import {
	type TransactionResponseBody,
	transactionResponseBody,
} from "../../metadata-collab-api-client";
import { initializationRejection, initializationSuccess } from "../../page-local-storage/server";

export const pageLocalStorageInitializeEvent = z.object({
	type: z.literal("initialize"),
	canvasSpecifier,
});
export type PageLocalStorageInitializeEvent = z.infer<typeof pageLocalStorageInitializeEvent>;
export const transactionRequest = z.object({
	type: z.literal("enqueue-transaction"),
	transactionId: z.string(),
	lastVersionSeen: z.number(),
	patches: z.array(patch),
	extra: z.unknown().optional(),
});
export type TransactionRequest = z.infer<typeof transactionRequest>;

export const updateBroadcastMessage = z.object({
	type: z.literal("transaction-broadcast"),
	version: z.number(),
	transactionProcessedEvent: transactionProcessedSuccess,
});
export const broadcastMessage = z.union([
	updateBroadcastMessage,
	disconnectMessageBroadcast,
	pongMessage,
]);
export type BroadcastMessage = z.infer<typeof broadcastMessage>;

export const initializeResult = z.union([initializationSuccess, initializationRejection]);
export type InitializeResult = z.infer<typeof initializeResult>;

export class PageLocalStorageApiClient {
	constructor(private _fetch: (init?: RequestInit | undefined) => Promise<Response>) {}

	async initialize(canvasSpecifier: CanvasSpecifier): Promise<InitializeResult> {
		const request: PageLocalStorageInitializeEvent = {
			type: "initialize",
			canvasSpecifier,
		};
		const response = await this._fetch(post(createHttpHeaders(accessToken()), request));
		if (response.status == 401) {
			throw new UnauthorizedError();
		}
		const json = await response.json();
		return initializeResult.parse(json);
	}

	async enqueueTransaction(
		transactionId: string,
		patches: Patch[],
		lastVersionSeen: number
	): Promise<TransactionResponseBody> {
		const request: TransactionRequest = {
			type: "enqueue-transaction",
			transactionId,
			patches,
			lastVersionSeen,
		};
		const response = await this._fetch(post(createHttpHeaders(accessToken()), request));
		if (response.status == 401) {
			throw new UnauthorizedError();
		}
		const json = await response.json();
		return transactionResponseBody.parse(json);
	}
}
